import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Divider, Button } from '@mui/material';
import './AboutSecondSection.scss';
import fblogo from '../../../assets/Images/About/fblogoblack.svg';
import linkdln from '../../../assets/Images/About/linkdlnblack.svg';
import insta from '../../../assets/Images/About/instablack.svg';
import youtube from '../../../assets/Images/About/youtubeblack.svg';
import { useLocation } from 'react-router-dom';

const sections = [
    { id: 0, title: "The Story Behind Our Journey" },
    { id: 1, title: "Industry Challenges" },
    { id: 2, title: "Our Solution" },
    { id: 3, title: "Why Choose Us?" },
    { id: 4, title: "Contact Us" }
];

// Flattened, paginated content — one entry per Previous/Next "page".
// Section 0 (the full company history) is long enough that reading it
// as one panel means endless scrolling, so it is split across three
// pages here; every other section is still a single page. `sectionId`
// ties a page back to its side-menu entry.
const pages = [
    {
        sectionId: 0,
        content: (
            <>
                <Typography className="content-text">
                    What started as a small initiative to help independent artists release their music has grown
                    into a broader ecosystem supporting artists, labels, and music businesses through distribution,
                    promotion, branding, production, and digital services.
                </Typography>

                <Typography variant="h6" className="content-subtitle">November 2019 — The Beginning</Typography>
                <Typography className="content-text">
                    FM Digital was founded by Ashish Rajput under its original name, FM FEEL Music.
                </Typography>
                <Typography className="content-text">
                    The idea was simple: create a supportive record-label environment where independent artists
                    could release their music on YouTube without having to bear the initial cost of distribution.
                </Typography>
                <Typography className="content-text">
                    As the platform began working with artists, we quickly encountered the realities of the
                    independent music industry — limited budgets, complicated distribution processes, challenges
                    in marketing, and difficulties in managing and sharing music revenue.
                </Typography>
                <Typography className="content-text">
                    These challenges became the foundation for building something bigger.
                </Typography>

                <Typography variant="h6" className="content-subtitle">November 2021 — The Birth of FM Digital</Typography>
                <Typography className="content-text">
                    FM FEEL Music was rebranded as FM Digital, marking an important new chapter in the company's journey.
                </Typography>
                <Typography className="content-text">
                    The focus expanded beyond being a record label. FM Digital began working toward making
                    professional music distribution and digital support more accessible to independent artists and labels.
                </Typography>
                <Typography className="content-text">
                    Over the following years, FM Digital continued building its catalog, services, artist
                    relationships, and industry partnerships while developing a stronger understanding of the
                    needs of independent music creators.
                </Typography>
            </>
        ),
    },
    {
        sectionId: 0,
        content: (
            <>
                <Typography variant="h6" className="content-subtitle">February 2023 — Launch of the FM Digital Distribution Platform</Typography>
                <Typography className="content-text">
                    In February 2023, FM Digital officially launched its own music distribution platform under
                    the FM Digital name, marking a major milestone in its journey.
                </Typography>
                <Typography className="content-text">
                    The platform began providing music distribution across 190+ digital music platforms, enabling
                    independent artists and labels to take their music to a wider global audience through a
                    single distribution system.
                </Typography>
                <Typography className="content-text">
                    Alongside digital distribution, FM Digital expanded its services to support artists and
                    labels with UGC monetization, YouTube Content ID, CMS-related services, and other digital
                    rights and content management solutions.
                </Typography>
                <Typography className="content-text">
                    This marked the beginning of FM Digital's dedicated journey as a music distribution
                    platform, with a growing focus on helping independent artists and labels manage, distribute,
                    monetize, and protect their music across the digital ecosystem.
                </Typography>

                <Typography variant="h6" className="content-subtitle">2024 — Koko Advertising & Marketing</Typography>
                <Typography className="content-text">
                    As the needs of artists and labels expanded beyond music distribution, Ashish Rajput
                    established Koko Advertising & Marketing as a broader creative and marketing venture.
                </Typography>
                <Typography className="content-text">Koko was created to provide services across:</Typography>
                <Box component="ul" className="content-list">
                    <li>Artist & Label Promotion</li>
                    <li>Brand Development</li>
                    <li>Digital Marketing</li>
                    <li>Audio & Video Production</li>
                    <li>Motion Graphics & Creative Content</li>
                    <li>Branding & Social Media</li>
                    <li>Marketing Campaigns</li>
                    <li>Commercial Production</li>
                </Box>
                <Typography className="content-text">
                    The vision was to create a company that could support artists, labels, and businesses not
                    only in releasing their work, but also in building their identity, promoting their content,
                    and growing their audience.
                </Typography>
            </>
        ),
    },
    {
        sectionId: 0,
        content: (
            <>
                <Typography variant="h6" className="content-subtitle">2026 — The Evolution of FMDO</Typography>
                <Typography className="content-text">
                    In 2026, FM Digital entered a new phase of growth, beginning with its rebrand as FMDO —
                    FM Digital Official™ in January, supported by a new identity and visual direction.
                </Typography>
                <Typography className="content-text">
                    By March 2026, FMDO had distributed 3,000+ quality songs and generated more than 1.2
                    billion streams through its distribution network, supported by a dedicated six-member
                    team across operations, distribution, artist support, content, and business development.
                </Typography>
                <Typography className="content-text">
                    In April 2026, FMDO officially became a subsidiary of Koko Advertising & Marketing, with
                    Koko serving as its parent company and official royalties and business partner.
                </Typography>

                <Box className="org-diagram">
                    <Box className="org-node">
                        <Typography className="org-node-title">Koko Advertising & Marketing</Typography>
                        <Typography className="org-node-subtitle">Parent Company & Business Partner</Typography>
                    </Box>
                    <Box className="org-arrow" aria-hidden="true">↓</Box>
                    <Box className="org-node">
                        <Typography className="org-node-title">FMDO — FM Digital Official™</Typography>
                        <Typography className="org-node-subtitle">Music Distribution & Artist/Label Services</Typography>
                    </Box>
                </Box>

                <Typography className="content-text">
                    This transition enables FMDO to focus on distributing, monetizing, protecting, and growing
                    music, while leveraging Koko's broader business, creative, financial, and operational ecosystem.
                </Typography>

                <Typography variant="h6" className="content-subtitle">Where We Are Today</Typography>
                <Typography className="content-text">
                    From a small idea started in 2019 to a growing music and creative ecosystem, our journey
                    has always been driven by one belief:
                </Typography>
                <Typography className="content-quote">
                    Independent artists and labels deserve access to professional tools, opportunities, and
                    support without having to build everything themselves.
                </Typography>
                <Typography className="content-text">
                    Today, FMDO and Koko Advertising & Marketing work together to build that ecosystem —
                    combining music distribution, artist and label support, marketing, branding, production,
                    and creative services under one growing business network.
                </Typography>
                <Typography className="content-closing">
                    The journey started with music.<br />
                    The vision is now much bigger.
                </Typography>
            </>
        ),
    },
    {
        sectionId: 1,
        content: (
            <>
                <Typography className="content-text">
                    Musicians have long struggled to receive fair payment for their work. According to Maria Bosseljon’s 2020 article
                    “Innovating in the Music Industry,” revenue often flows through a lengthy chain of streaming services, record labels,
                    publishers, and performing rights organizations before reaching the artists, leaving them with minimal returns.
                    For instance, artists earn just $0.0032 per stream on Spotify, $0.0056 on Apple Music, and $0.00436 on Deezer, making it
                    difficult for independent musicians to sustain their careers.
                </Typography>
                <Typography className="content-text">
                    Traditional distribution models often require artists to pay high upfront fees or surrender a portion of their royalties,
                    which can be burdensome for emerging talents. This typically leads artists to use free services that offer little to no revenue,
                    further complicating their financial stability. Moreover, high international transfer fees can be prohibitive for artists in
                    developing economies, compounding their challenges.
                </Typography>
            </>
        ),
    },
    {
        sectionId: 2,
        content: (
            <Typography className="content-text">
                At FM Digital, we tackle these issues with a dual approach. Our free plan allows artists to distribute their music while retaining
                70% of their royalties. For those seeking greater control, our subscription plan offers 100% revenue retention. By combining DeFi,
                royalty capture, and AI technology, we provide a comprehensive platform that supports fair compensation and accessibility for all
                musicians, regardless of their location or stage in their career.
            </Typography>
        ),
    },
    {
        sectionId: 3,
        content: (
            <Typography className="content-text">
                At FM Digital, we empower independent artists and labels with a seamless, cost-effective music distribution solution. With our platform,
                you keep 100% of your revenue on our subscription plan and enjoy a straightforward, artist-friendly approach. From getting your music
                on over 210 global streaming platforms to providing industry-leading tools for promotion and growth, we’re dedicated to making your music
                journey as smooth and successful as possible. Choose FM Digital to take control of your music career with a partner who truly understands
                and supports your vision.
            </Typography>
        ),
    },
    {
        sectionId: 4,
        content: (
            <>
                <Typography className="content-text">
                    Got questions or need support? We’re here to help! Reach out to us via any of the following channels:
                </Typography>
                <Typography className="content-text">
                    <strong>Email:</strong> info@fmdigitalofficial.com<br />
                    <strong>Website:</strong> www.fmdigitalofficial.com<br />
                </Typography>
                <Box className="social-links">
                    <a href="https://in.linkedin.com/company/fmdigitalofficial" target="_blank" rel="noopener noreferrer">
                        <img src={linkdln} alt="LinkedIn Logo" style={{ color: 'black' }} />
                    </a>
                    <a href="https://www.instagram.com/fmdigitalofficial/ fmdigitalofficial/" target="_blank" rel="noopener noreferrer">
                        <img src={insta} alt="Instagram Logo" />
                    </a>
                    {/* <a href="https://www.facebook.com/FMDigital2/ FMDigital2/" target="_blank" rel="noopener noreferrer">
                        <img src={fblogo} alt="Facebook Logo" />
                    </a> */}
                    <a href="https://www.youtube.com/@fm_digital" target="_blank" rel="noopener noreferrer">
                        <img src={youtube} alt="YouTube Logo" />
                    </a>
                </Box>
            </>
        ),
    },
];

const AboutSecondSection = () => {
    const [currentPage, setCurrentPage] = useState(0);
    const contentTopRef = useRef(null);

    // Long pages get split across Previous/Next — without this, switching
    // pages while scrolled down on a long one leaves the reader mid-way
    // into the next page instead of at its start.
    useEffect(() => {
        contentTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [currentPage]);

    const currentSectionId = pages[currentPage].sectionId;
    const pagesInSection = pages.filter((p) => p.sectionId === currentSectionId);
    const pagePositionInSection = pages.slice(0, currentPage + 1).filter((p) => p.sectionId === currentSectionId).length;

    const goToSection = (sectionId) => {
        const firstPageIndex = pages.findIndex((p) => p.sectionId === sectionId);
        if (firstPageIndex !== -1) {
            setCurrentPage(firstPageIndex);
        }
    };

    const handleNext = () => {
        if (currentPage < pages.length - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePrevious = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    const location = useLocation()
    useEffect(() => {
        // Get the query parameter from the URL
        const params = new URLSearchParams(location.search);
        const sectionId = parseInt(params.get('section'));

        // If the sectionId is valid, navigate to that section's first page
        if (!isNaN(sectionId) && sectionId >= 0 && sectionId < sections.length) {
            goToSection(sectionId);
        }
    }, [location]);

    return (
        <Box className="about-second-section">
            <Box className="content-section">
                <Box className="content-box">
                    <Box className="side-menu">
                        <Typography variant="h6" className="menu-title">About</Typography>
                        {sections.map((sec) => (
                            <Typography
                                key={sec.id}
                                className={`menu-item ${currentSectionId === sec.id ? 'active' : ''}`}
                                onClick={() => goToSection(sec.id)}
                            >
                                {sec.title}
                                {currentSectionId === sec.id && <div className="active-line"></div>}
                            </Typography>
                        ))}
                    </Box>

                    <Box className="content">
                        <Box className="content-title-row" ref={contentTopRef}>
                            <Typography variant="h4" className="content-title">
                                {sections.find((sec) => sec.id === currentSectionId).title}
                            </Typography>
                            {pagesInSection.length > 1 && (
                                <Typography className="content-page-indicator">
                                    Part {pagePositionInSection} of {pagesInSection.length}
                                </Typography>
                            )}
                        </Box>
                        {pages[currentPage].content}
                    </Box>
                    {/* <Divider className="content-divider" /> */}
                </Box>
            </Box>
            <Box className="navigation-buttons">
                <Button
                    className="nav-button"
                    onClick={handlePrevious}
                    disabled={currentPage === 0}
                >
                    Previous Page
                </Button>
                <Button
                    className="nav-button"
                    onClick={handleNext}
                    disabled={currentPage === pages.length - 1}
                >
                    Next Page
                </Button>
            </Box>
        </Box>
    );
};

export default AboutSecondSection;
